import { Textarea } from '@/components/ui/textarea';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { Boxes, ArrowUp, ArrowDown, AlertTriangle, Download, Plus, MapPin, Package, TrendingUp, Undo2 } from 'lucide-react';
import { FormDialog } from '@/components/ui/form-dialog';
import { Button } from '@/components/ui/button';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup } from '@/components/ui/radio-group';
import { RecordLink } from '@/components/records/RecordLink';
import { TableRowActions, type TableRowAction } from '@/components/ui/table-row-actions';
import { ReverseAdjustmentDialog, type MovementReversal } from '@/components/inventory/ReverseAdjustmentDialog';
import type { RecordType } from '@/components/records/registry';
import { Card, CardContent } from '@/components/ui/card';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { SearchInput } from '@/components/ui/search-input';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { TabBar } from '@/components/ui/tab-bar';
import { useAuthStore } from '@/stores/auth.store';
import { useLocationStore } from '@/stores/location.store';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { StatCard } from '@/components/ui/stat-card';
import { getApiErrorMessage } from '@/lib/toast';
import { Combobox } from '@/components/ui/combobox';
import { ProductSearchCombobox } from '@/components/inventory/ProductSearchCombobox';
import { Dropdown } from 'primereact/dropdown';
import { ExpiryTrackingTab } from './ExpiryTrackingTab';

const ADJUSTMENT_TYPES = [
  { value: 'ADJUSTMENT_IN', label: 'Stock In (Add)' },
  { value: 'ADJUSTMENT_OUT', label: 'Stock Out (Remove)' },
  { value: 'DAMAGE', label: 'Damage / Write-off' },
] as const;

type MovementRow = {
  quantity: number;
  beforeQty?: number | null;
  afterQty?: number | null;
  type?: string;
};

/** List endpoints return either a bare array or `{ data, meta }`. */
interface ListEnvelope<T> {
  data?: T[];
  meta?: { total?: number };
}
type ListResponse<T> = T[] | ListEnvelope<T>;

function unwrapList<T>(res: ListResponse<T> | undefined): T[] {
  return Array.isArray(res) ? res : (res?.data ?? []);
}

const ADJUST_PRODUCT_FILTERS = { trackStock: 'true' };

interface InventoryProductRow {
  id: string;
  name: string;
  sku?: string | null;
  serialTracking?: boolean;
  expiryTracking?: boolean;
  stockQuantity: number;
  costPrice: number;
  salePrice: number;
  sellingPrice?: number;
  price?: number;
  categoryName?: string;
  category?: { name?: string } | null;
  reorderPoint?: number | null;
}

interface LocationRow {
  id: string;
  name: string;
}

interface LocationStockRow {
  productId: string;
  quantity: number;
}

interface SerialNumberRow {
  id: string;
  serialNo: string;
  notes?: string | null;
}

interface AdjustPayload {
  productId: string;
  quantity: number;
  type: string;
  reason: string;
  locationId?: string;
  serialNumbers?: string[];
  batchNo?: string;
  expiryDate?: string;
  batchId?: string;
  treatment?: 'WRITE_OFF' | 'CORRECTION';
  confirmLargeValue?: boolean;
}

/** Entries at or above this value need an explicit confirmation (mirrors the API guard). */
const LARGE_VALUE_LIMIT = 100_000;

/** A received lot of an expiry-tracked product (GET /inventory/batches). */
interface BatchRow {
  id: string;
  batchNo: string | null;
  quantity: number;
  expiryDate: string | null;
}

type ForecastUrgency = 'critical' | 'warning' | 'ok';

interface DemandForecastRow {
  productId: string;
  productName: string;
  sku?: string | null;
  currentStock: number;
  unitsSold90d: number;
  dailyVelocity: number;
  daysOfStockRemaining: number | null;
  suggestedReorderQty: number;
  urgency: ForecastUrgency;
}

interface ReorderAlertRow {
  id: string;
  name: string;
  sku?: string | null;
  stock_quantity: number;
  reorder_point?: number | null;
  effective_reorder_point?: number | null;
  reorder_quantity?: number | null;
  lead_time_days?: number | null;
}

type ReorderAlertsResponse = ReorderAlertRow[] | (ListEnvelope<ReorderAlertRow> & { total?: number });

interface LowStockResponse {
  total?: number;
  data?: unknown[];
}

interface StockMovementRow extends MovementRow {
  id: string;
  createdAt: string;
  reason?: string | null;
  product?: { name?: string } | null;
  location?: { name?: string } | null;
  serialNumber?: { serialNo?: string } | null;
  /** POS-I237: OPENING | CORRECTION | WRITE_OFF | null, set by the API. */
  treatment?: string | null;
  journalEntry?: { id: string; entryNumber: string; status: string } | null;
  /** POS-I239: the document it belongs to, resolved by the API from the stored reference (type null = plain text). */
  reference?: { type: string | null; id: string; label: string } | null;
  /** POS-I244: Reverse adjustment state, computed by the API. */
  reversal?: MovementReversal | null;
}

/** Quantity change cell: one direction icon + absolute stored quantity (sign lives in storage only). */
function movementChangeIsDown(m: MovementRow): boolean {
  if (m.afterQty != null && m.beforeQty != null) {
    if (m.afterQty < m.beforeQty) return true;
    return m.quantity < 0;
  }
  return !['ADJUSTMENT_IN', 'PURCHASE', 'RETURN'].includes(m.type ?? '');
}

/** POS-I244: the Actions cell of a Movement History row, drawn from the API's reversal block. */
function MovementReverseCell({ m, onReverse }: { m: StockMovementRow; onReverse: () => void }) {
  const { t } = useTranslation('inventory');
  const r = m.reversal;
  if (!r || r.state === 'NOT_REVERSIBLE') return null;
  if (r.state === 'REVERSED') {
    return <span className="text-xs text-muted-foreground">{t('reverseAlready')}</span>;
  }
  const blocked = r.state === 'BLOCKED';
  const reasonText =
    r.reasonCode === 'STOCK_TOO_LOW'
      ? t('reverseBlockedTooLow', { onHand: r.onHand ?? 0, qty: r.qty })
      : r.reasonCode === 'LOCATION_INACTIVE'
        ? t('reverseBlockedLocation')
        : r.reasonCode === 'TRACKED_PRODUCT'
          ? t('reverseBlockedTracked')
          : undefined;
  const actions: TableRowAction[] = [
    {
      key: 'reverse',
      label: t('reverseAction'),
      icon: Undo2,
      primary: true,
      labeled: true,
      labelClassName: 'mov-reverse-label',
      disabled: blocked,
      disabledReason: blocked ? reasonText : undefined,
      onSelect: onReverse,
    },
  ];
  return <TableRowActions actions={actions} />;
}

function StockAdjustmentDialog({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { t } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState<number>(1);
  const [type, setType] = useState<string>(ADJUSTMENT_TYPES[0].value);
  const [reason, setReason] = useState('');
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  // Serial number state
  const [serialInput, setSerialInput] = useState(''); // textarea for ADJUSTMENT_IN
  const [selectedSerialIds, setSelectedSerialIds] = useState<string[]>([]); // for OUT/DAMAGE
  // Expiry batch state (expiry-tracked products): received lot on stock-in, lot to draw down on stock-out
  const [batchNo, setBatchNo] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [batchId, setBatchId] = useState('');
  // POS-I237: what a Stock In / Stock Out is (real loss or received vs a count correction) and the large-value confirm
  const [treatment, setTreatment] = useState<'WRITE_OFF' | 'CORRECTION'>('WRITE_OFF');
  const [confirmLarge, setConfirmLarge] = useState(false);
  const { hasPermission } = useAuthStore();
  const canCorrect = hasPermission('accounting:create');

  // POS-I257: the product picker searches the server (ProductSearchCombobox); only the chosen product is kept here
  const [selectedProduct, setSelectedProduct] = useState<InventoryProductRow | null>(null);

  const { data: locationsForAdjust } = useQuery({
    queryKey: ['locations-for-adjust'],
    queryFn: async () => {
      const res = await api.get<ListResponse<LocationRow>>('/locations?limit=100');
      return res.data;
    },
  });

  const isSerialTracked: boolean = !!productId && selectedProduct?.serialTracking === true;
  const isExpiryTracked: boolean = !!productId && selectedProduct?.expiryTracking === true;

  const { data: productBatches } = useQuery({
    queryKey: ['batches-for-adjust', productId],
    queryFn: async () => {
      const res = await api.get<BatchRow[]>(`/inventory/batches?productId=${productId}`);
      return res.data;
    },
    enabled: isExpiryTracked && !!productId && type !== 'ADJUSTMENT_IN',
  });

  const parsedSerials = useMemo(
    () => serialInput.split('\n').map((s) => s.trim()).filter(Boolean),
    [serialInput],
  );

  // For serial-tracked products, quantity is derived from what the user enters
  const effectiveQuantity = isSerialTracked
    ? type === 'ADJUSTMENT_IN'
      ? parsedSerials.length
      : selectedSerialIds.length
    : quantity;

  // Fetch available serial numbers for OUT/DAMAGE on serial-tracked products
  const { data: availableSerials } = useQuery({
    queryKey: ['available-serials-for-adjust', productId, type],
    queryFn: async () => {
      const res = await api.get<ListEnvelope<SerialNumberRow>>(`/serial-numbers?productId=${productId}&status=AVAILABLE&limit=500`);
      return res.data;
    },
    enabled: isSerialTracked && !!productId && type !== 'ADJUSTMENT_IN',
  });

  // Reset serial state when product or type changes
  const handleProductChange = (val: string, product: InventoryProductRow | null = null) => {
    setProductId(val);
    setSelectedProduct(product);
    setSerialInput('');
    setSelectedSerialIds([]);
    setQuantity(1);
    setBatchNo('');
    setExpiryDate('');
    setBatchId('');
    setConfirmLarge(false);
  };
  const handleTypeChange = (val: string) => {
    setType(val || ADJUSTMENT_TYPES[0].value);
    setTreatment('WRITE_OFF');
    setConfirmLarge(false);
    setSerialInput('');
    setSelectedSerialIds([]);
    setBatchId('');
  };

  const toggleSerialId = (id: string) => {
    setSelectedSerialIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const mutation = useMutation({
    mutationFn: async (payload: AdjustPayload) => {
      const res = await api.post('/inventory/adjust', payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Stock adjustment saved');
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['low-stock'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products-pos'] });
      queryClient.invalidateQueries({ queryKey: ['expiring-batches'] });
      queryClient.invalidateQueries({ queryKey: ['batches-for-adjust'] });
      onClose();
    },
    onError: (err: unknown) => {
      toast.error(getApiErrorMessage(err, 'Failed to save adjustment'));
    },
  });

  // POS-I237: the accounting effect, computed from the product's cost (the API recomputes it)
  const isCorrection = treatment === 'CORRECTION' && type !== 'DAMAGE';
  const unitCost = Number(selectedProduct?.costPrice ?? 0);
  const entryValue = Math.round(effectiveQuantity * unitCost * 100) / 100;
  const needsLargeConfirm = !isCorrection && !!selectedProduct && entryValue >= LARGE_VALUE_LIMIT;
  const reasonOk = isCorrection ? reason.trim().length >= 5 : reason.trim().length > 0;

  const canSubmit =
    !!productId &&
    effectiveQuantity > 0 &&
    reasonOk &&
    (!needsLargeConfirm || confirmLarge) &&
    // an expiry-tracked lot must say when it expires
    !(isExpiryTracked && type === 'ADJUSTMENT_IN' && !expiryDate);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload: AdjustPayload = { productId, quantity: effectiveQuantity, type, reason: reason.trim() };
    if (isCorrection) payload.treatment = 'CORRECTION';
    if (needsLargeConfirm && confirmLarge) payload.confirmLargeValue = true;
    if (locationId) payload.locationId = locationId;
    if (isExpiryTracked && type === 'ADJUSTMENT_IN') {
      if (batchNo.trim()) payload.batchNo = batchNo.trim();
      payload.expiryDate = expiryDate;
    }
    if (isExpiryTracked && type !== 'ADJUSTMENT_IN' && batchId) payload.batchId = batchId;
    if (isSerialTracked) {
      payload.serialNumbers = type === 'ADJUSTMENT_IN' ? parsedSerials : selectedSerialIds;
    }
    mutation.mutate(payload);
  };

  return (
    <FormDialog
      open={visible}
      onClose={onClose}
      title={t('stockAdjustmentTitle')}
      width="52rem"
      maximizable
      submitLabel={t('saveAdjustment')}
      cancelLabel={t('cancel')}
      isPending={mutation.isPending}
      submitDisabled={!canSubmit}
      onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>{t('product')}</Label>
          <ProductSearchCombobox<InventoryProductRow>
            value={productId}
            onChange={handleProductChange}
            selected={selectedProduct}
            filters={ADJUST_PRODUCT_FILTERS}
            placeholder="Select a product…"
            searchPlaceholder="Search by name, SKU or barcode..."
          />
          {isSerialTracked && (
            <p className="text-xs text-info-text font-medium">
              🔖 Serial / IMEI tracked — enter serial numbers below instead of quantity
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>{t('type')}</Label>
          <Combobox
            options={[
              { value: 'ADJUSTMENT_IN', label: t('adjustmentIn') },
              { value: 'ADJUSTMENT_OUT', label: t('adjustmentOut') },
              { value: 'DAMAGE', label: t('damage') },
            ]}
            value={type}
            onChange={handleTypeChange}
            placeholder="Select type..."
            searchPlaceholder="Search types..."
          />
        </div>

        {/* Quantity — hidden for serial-tracked products (derived from serial count) */}
        {!isSerialTracked && (
          <div className="space-y-1.5">
            <Label>{t('quantityLabel')}</Label>
            <Input
              type="number"
              min={1}
              step={1}
              value={quantity || ''}
              onChange={(e) => setQuantity(Number(e.target.value))}
              placeholder="Enter quantity"
            />
          </div>
        )}

        {/* POS-I237: Stock In / Stock Out is either real (loss / received) or a count correction */}
        {type !== 'DAMAGE' && (
          <div className="space-y-1.5">
            <Label>{t('treatmentLabel')}</Label>
            <RadioGroup
              name={t('treatmentLabel')}
              value={isCorrection ? 'CORRECTION' : 'WRITE_OFF'}
              onChange={(v) => { setTreatment(v as 'WRITE_OFF' | 'CORRECTION'); setConfirmLarge(false); }}
              options={[
                {
                  value: 'WRITE_OFF',
                  label: type === 'ADJUSTMENT_OUT' ? t('treatmentOutWriteOff') : t('treatmentInReceived'),
                },
                {
                  value: 'CORRECTION',
                  label: type === 'ADJUSTMENT_OUT' ? t('treatmentOutCorrection') : t('treatmentInCorrection'),
                  disabled: !canCorrect,
                  hint: t('correctionNeedsRights'),
                },
              ]}
            />
          </div>
        )}

        {/* Live accounting effect */}
        {productId && effectiveQuantity > 0 && (
          <p className="text-sm text-muted-foreground" data-testid="adjust-effect">
            {unitCost <= 0
              ? t('helperNoCost')
              : isCorrection
                ? t('helperCorrection', { amount: formatCurrency(entryValue) })
                : type === 'ADJUSTMENT_IN'
                  ? t('helperGain', { amount: formatCurrency(entryValue) })
                  : t('helperWriteOff', { amount: formatCurrency(entryValue) })}
          </p>
        )}
        {needsLargeConfirm && (
          <Label className="flex items-start gap-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 cursor-pointer">
            <Checkbox checked={confirmLarge} onChange={(e) => setConfirmLarge(e.target.checked)} />
            <span className="text-sm">
              <span className="block font-medium">{t('confirmValue', { amount: formatCurrency(entryValue) })}</span>
              <span className="block text-xs text-muted-foreground">{t('largeValueNotice', { amount: formatCurrency(entryValue) })}</span>
            </span>
          </Label>
        )}

        {/* Serial numbers — ADJUSTMENT_IN: bulk textarea (shown immediately when serial-tracked) */}
        {isSerialTracked && type === 'ADJUSTMENT_IN' && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Serial / IMEI Numbers</Label>
              {parsedSerials.length > 0 && (
                <span className="text-xs font-medium text-success-text">
                  {parsedSerials.length} serial{parsedSerials.length !== 1 ? 's' : ''} entered
                </span>
              )}
            </div>
            <Textarea
              className="min-h-[140px] font-mono resize-y"
              placeholder="Enter serial numbers, one per line&#10;e.g.&#10;SN001234&#10;SN001235&#10;SN001236"
              value={serialInput}
              onChange={(e) => setSerialInput(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Quantity will be set automatically from the number of serials entered.
            </p>
          </div>
        )}

        {/* Serial numbers — ADJUSTMENT_OUT / DAMAGE: pick from available serials */}
        {isSerialTracked && type !== 'ADJUSTMENT_IN' && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Select Serials to Remove</Label>
              {selectedSerialIds.length > 0 && (
                <span className="text-xs font-medium text-success-text">
                  {selectedSerialIds.length} selected
                </span>
              )}
            </div>
            <div className="border border-input rounded-md max-h-48 overflow-y-auto divide-y divide-border">
              {(availableSerials?.data || []).length === 0 ? (
                <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                  No available serial numbers for this product.
                </p>
              ) : (
                (availableSerials?.data || []).map((sn) => (
                  <Label key={sn.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50">
                    <Checkbox
                      checked={selectedSerialIds.includes(sn.id)}
                      onChange={() => toggleSerialId(sn.id)}
                    />
                    <span className="font-mono text-sm">{sn.serialNo}</span>
                    {sn.notes && <span className="text-xs text-muted-foreground ms-auto">{sn.notes}</span>}
                  </Label>
                ))
              )}
            </div>
          </div>
        )}

        {/* Expiry batch: stock-in records the received lot */}
        {isExpiryTracked && type === 'ADJUSTMENT_IN' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('batchNoLabel')}</Label>
              <Input value={batchNo} maxLength={60} onChange={(e) => setBatchNo(e.target.value)} placeholder={t('batchNoPlaceholder')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('expiryDateLabel')}</Label>
              <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </div>
            <p className="text-xs text-muted-foreground sm:col-span-2">{t('expiryBatchHint')}</p>
          </div>
        )}

        {/* Expiry batch: stock-out can draw down a specific lot (e.g. write off an expired one) */}
        {isExpiryTracked && type !== 'ADJUSTMENT_IN' && (
          <div className="space-y-1.5">
            <Label>{t('batchToTakeOut')}</Label>
            <Dropdown
              value={batchId}
              onChange={(e) => setBatchId(e.value || '')}
              options={[
                { label: t('batchAnyOption'), value: '' },
                ...(productBatches ?? [])
                  .filter((b) => b.quantity > 0)
                  .map((b) => ({
                    label: `${b.batchNo ?? t('expiryNoBatchNo')} · ${b.quantity} · ${b.expiryDate ? b.expiryDate.slice(0, 10) : t('noExpiryDate')}`,
                    value: b.id,
                  })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('batchAnyOption')}
              className="w-full mt-1"
            />
          </div>
        )}

        <div className="space-y-1.5">
          <Label>{t('reasonLabel')}</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('adjustmentReason')}
          />
          {isCorrection && reason.trim().length > 0 && reason.trim().length < 5 && (
            <p className="text-xs text-destructive-text">{t('correctionReasonRequired')}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>{t('locationOptional')}</Label>
          <Dropdown
            value={locationId ?? ''}
            onChange={(e) => setLocationId(e.value || undefined)}
            options={[{ label: t('allLocationsGlobal'), value: '' }, ...unwrapList(locationsForAdjust).map((loc) => ({ label: loc.name, value: loc.id }))]}
            optionLabel="label"
            optionValue="value"
            placeholder={t('allLocationsGlobal')}
            className="w-full mt-1"
          />
        </div>
      </div>
    </FormDialog>
  );
}

function DemandForecastTab() {
  const { data: forecasts, isLoading } = useQuery<ListResponse<DemandForecastRow>>({
    queryKey: ['demand-forecasts'],
    queryFn: () => api.get<ListResponse<DemandForecastRow>>('/inventory/demand-forecasts').then((r) => r.data),
    staleTime: 120_000,
  });
  const [search, setSearch] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState<'' | ForecastUrgency>('');

  const filtered = unwrapList(forecasts).filter((f) => {
    const matchSearch = !search || f.productName.toLowerCase().includes(search.toLowerCase()) || (f.sku ?? '').toLowerCase().includes(search.toLowerCase());
    const matchUrgency = !urgencyFilter || f.urgency === urgencyFilter;
    return matchSearch && matchUrgency;
  });

  const urgencyConfig: Record<string, { label: string; className: string }> = {
    critical: { label: 'Critical', className: 'bg-destructive/10 text-destructive-text' },
    warning: { label: 'Warning', className: 'bg-warning/10 text-warning-text' },
    ok: { label: 'OK', className: 'bg-success/10 text-success-text' },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/10 border border-primary/30">
        <TrendingUp className="h-5 w-5 text-primary-text mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-primary-text">Sales Velocity Forecast</p>
          <p className="text-xs text-primary-text mt-0.5">
            Based on last 90 days of sales. Daily velocity is used to estimate days of stock remaining and suggest reorder quantities.
          </p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <SearchInput value={search} onChange={setSearch} placeholder="Search product..." className="w-60" />
        <Dropdown
          value={urgencyFilter}
          onChange={(e) => setUrgencyFilter(e.value as '' | ForecastUrgency)}
          options={[
            { label: 'All urgencies', value: '' },
            { label: 'Critical', value: 'critical' },
            { label: 'Warning', value: 'warning' },
            { label: 'OK', value: 'ok' },
          ]}
          optionLabel="label"
          optionValue="value"
          placeholder="All urgencies"
        />
      </div>

      {isLoading ? (
        <SkeletonTable rows={8} cols={7} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={TrendingUp} title="No forecast data" description="Make some sales to start seeing demand forecasts." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-muted/50">
                <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground">Product</th>
                <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Stock</th>
                <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sold (90d)</th>
                <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Daily Velocity</th>
                <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Days Left</th>
                <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-muted-foreground">Suggested Reorder</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">Urgency</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((f) => {
                const uc = urgencyConfig[f.urgency] ?? urgencyConfig.ok;
                return (
                  <tr key={f.productId} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">{f.productName}</div>
                      {f.sku && <div className="text-xs text-muted-foreground">{f.sku}</div>}
                    </td>
                    <td className="px-4 py-3 text-end text-sm font-mono">{f.currentStock}</td>
                    <td className="px-4 py-3 text-end text-sm font-mono">{f.unitsSold90d}</td>
                    <td className="px-4 py-3 text-end text-sm font-mono">{f.dailyVelocity}/day</td>
                    <td className="px-4 py-3 text-end text-sm font-mono">
                      {f.daysOfStockRemaining !== null ? `${f.daysOfStockRemaining}d` : '∞'}
                    </td>
                    <td className="px-4 py-3 text-end text-sm font-mono font-semibold">{f.suggestedReorderQty}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${uc.className}`}>
                        {uc.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReorderAlertsCard({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation('inventory');
  const reorderLocationId = useLocationStore((st) => st.selectedLocationId);
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data: alerts, isLoading } = useQuery({
    queryKey: ['reorder-alerts', reorderLocationId],
    queryFn: () =>
      api
        .get<ReorderAlertsResponse>('/inventory/reorder-alerts', { params: reorderLocationId ? { locationId: reorderLocationId } : undefined })
        .then((r) => r.data),
    staleTime: 60_000,
  });
  const rows: ReorderAlertRow[] = unwrapList(alerts);

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  }

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function createPOForSelected() {
    const selectedRows = rows.filter((r) => selected.has(r.id));
    navigate('/purchasing/orders', {
      state: {
        prefillPO: {
          items: selectedRows.map((p) => ({
            productId: p.id,
            productName: p.name,
            quantity: p.reorder_quantity || 1,
          })),
        },
      },
    });
  }

  const inner = (
    <>
      <div className="flex items-center justify-between mb-3">
        {!embedded && (
          <div className="flex items-center gap-2 text-lg font-semibold">
            <AlertTriangle className="h-5 w-5 text-warning-text" />
            {t('reorderAlerts')}
            {rows.length > 0 && (
              <span className="ms-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-text">
                {rows.length}
              </span>
            )}
          </div>
        )}
        {selected.size > 0 && (
          <Button size="sm" onClick={createPOForSelected} className={embedded ? '' : 'ms-auto'}>
            Create PO for {selected.size} item{selected.size > 1 ? 's' : ''}
          </Button>
        )}
      </div>
      {isLoading ? (
        <SkeletonTable rows={3} cols={5} />
      ) : rows.length === 0 ? (
        <EmptyState icon={Package} title={t('noReorderAlerts')} description={t('allAboveReorderPoints')} />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="w-10 px-4 py-2.5">
                  <Checkbox
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    />
                  </th>
                  <th className="px-4 py-2.5 text-start font-medium">{t('product')}</th>
                  <th className="px-4 py-2.5 text-start font-medium">{t('sku')}</th>
                  <th className="px-4 py-2.5 text-end font-medium">{t('currentStock')}</th>
                  <th className="px-4 py-2.5 text-end font-medium">{t('reorderPoint')}</th>
                  <th className="px-4 py-2.5 text-end font-medium">{t('suggestedQty')}</th>
                  <th className="px-4 py-2.5 text-end font-medium">{t('leadTime')}</th>
                  <th className="px-4 py-2.5 text-center font-medium">{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    className={`border-b transition-colors cursor-pointer ${selected.has(p.id) ? 'bg-warning/5' : 'hover:bg-accent/30'}`}
                    onClick={() => toggleRow(p.id)}
                  >
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(p.id)}
                        onChange={() => toggleRow(p.id)}
                      />
                    </td>
                    <td className="px-4 py-2.5 font-medium">{p.name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs font-mono">{p.sku ?? '—'}</td>
                    <td className="px-4 py-2.5 text-end">
                      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive-text">
                        <AlertTriangle className="h-3 w-3" />
                        {p.stock_quantity}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-end font-mono text-sm">{p.effective_reorder_point ?? p.reorder_point}</td>
                    <td className="px-4 py-2.5 text-end font-mono text-sm">{p.reorder_quantity ?? '—'}</td>
                    <td className="px-4 py-2.5 text-end text-muted-foreground">{p.lead_time_days != null ? `${p.lead_time_days}d` : '—'}</td>
                    <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => navigate('/purchasing/orders', {
                          state: {
                            prefillPO: {
                              items: [{ productId: p.id, productName: p.name, quantity: p.reorder_quantity || 1 }],
                            },
                          },
                        })}
                      >
                        {t('createPo')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
  );

  if (embedded) return inner;

  return (
    <Card className="animate-fade-in-up">
      <CardContent className="pt-5">{inner}</CardContent>
    </Card>
  );
}

function ExportMovementsButton({ movSearch, movType, movLocationId }: { movSearch: string; movType: string; movLocationId: string }) {
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (movSearch) params.set('search', movSearch);
      if (movType) params.set('type', movType);
      if (movLocationId) params.set('locationId', movLocationId);
      const res = await api.get(`/inventory/movements/export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock-movements-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Movements exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
      <Download className="me-1.5 h-4 w-4" />
      {exporting ? 'Exporting…' : 'Export CSV'}
    </Button>
  );
}

export function InventoryPage() {
  const { t } = useTranslation('inventory');
  const [activeTab, setActiveTab] = useState<'stock' | 'movements' | 'alerts' | 'forecast' | 'expiry'>('stock');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [movPage, setMovPage] = useState(1);
  const [movSearch, setMovSearch] = useState('');
  const [movType, setMovType] = useState('');
  const [movLocationId, setMovLocationId] = useState('');
  const { hasPermission } = useAuthStore();
  const canAdjust = hasPermission('inventory:update') || hasPermission('inventory:*');
  const canViewLocations = hasPermission('locations:*') || hasPermission('locations:read');
  // POS-I244: the Actions column exists only for users who may reverse (inventory:update and accounting:create)
  const canReverse = hasPermission('inventory:update') && hasPermission('accounting:create');
  const [reverseTarget, setReverseTarget] = useState<StockMovementRow | null>(null);

  const { selectedLocationId, selectedLocationName } = useLocationStore();

  const queryClient = useQueryClient();

  // Real-time: refresh stock when any update arrives
  const handleStockUpdated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
    queryClient.invalidateQueries({ queryKey: ['location-stock'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  }, [queryClient]);
  useSocketEvent('stock.updated', handleStockUpdated);
  useSocketEvent('purchase.received', handleStockUpdated);
  useSocketEvent('transfer.updated', handleStockUpdated);

  const { data: products, isLoading } = useQuery({
    queryKey: ['inventory-products', search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      const res = await api.get<ListEnvelope<InventoryProductRow>>(`/products?${params}`);
      return res.data;
    },
  });

  // POS-I257: ask for the location stock of exactly the products on this page (was: the first 100 location rows,
  // so products further down the list showed 0 at a location)
  const visibleProductIds = (products?.data ?? []).map((p) => p.id);
  const { data: locationStock } = useQuery({
    queryKey: ['location-stock', selectedLocationId, visibleProductIds.join(',')],
    queryFn: async () => {
      const res = await api.get<ListEnvelope<LocationStockRow>>(
        `/locations/${selectedLocationId}/stock?limit=${Math.max(visibleProductIds.length, 1)}&productIds=${visibleProductIds.join(',')}`,
      );
      return res.data;
    },
    enabled: canViewLocations && !!selectedLocationId && visibleProductIds.length > 0,
    staleTime: 30_000,
  });

  const locationStockMap = new Map<string, number>(
    (locationStock?.data ?? []).map((ls): [string, number] => [ls.productId, ls.quantity])
  );

  const { data: lowStock } = useQuery({
    queryKey: ['low-stock', selectedLocationId],
    queryFn: async () => {
      const res = await api.get<LowStockResponse>('/inventory/low-stock', {
        params: selectedLocationId ? { locationId: selectedLocationId } : undefined,
      });
      return res.data;
    },
  });

  const { data: movements } = useQuery({
    queryKey: ['stock-movements'],
    queryFn: async () => {
      const res = await api.get<ListEnvelope<StockMovementRow>>('/inventory/movements?limit=5');
      return res.data;
    },
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations-list-for-movements'],
    queryFn: async () => {
      const res = await api.get<ListResponse<LocationRow>>('/locations?limit=100');
      return res.data;
    },
    enabled: canViewLocations,
  });
  const locationsList: LocationRow[] = unwrapList(locationsData);

  const { data: movData, isLoading: movLoading } = useQuery({
    queryKey: ['inventory-movements-full', movPage, movSearch, movType, movLocationId],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(movPage), limit: '20' });
      if (movSearch) params.set('search', movSearch);
      if (movType) params.set('type', movType);
      if (movLocationId) params.set('locationId', movLocationId);
      const res = await api.get<ListResponse<StockMovementRow>>(`/inventory/movements?${params}`);
      return res.data;
    },
    enabled: activeTab === 'movements',
  });

  const recentMovementsTotal = movements?.meta?.total ?? 0;

  const { data: reorderAlerts } = useQuery({
    queryKey: ['reorder-alerts', selectedLocationId],
    queryFn: () =>
      api
        .get<ReorderAlertsResponse>('/inventory/reorder-alerts', { params: selectedLocationId ? { locationId: selectedLocationId } : undefined })
        .then((r) => r.data),
    staleTime: 60_000,
  });
  const reorderAlertRows: ReorderAlertRow[] = unwrapList(reorderAlerts);
  const reorderAlertsTotal: number | undefined = Array.isArray(reorderAlerts) ? undefined : reorderAlerts?.total;
  const movRows: StockMovementRow[] = unwrapList(movData);
  const movTotal: number = Array.isArray(movData) ? 0 : (movData?.meta?.total ?? 0);

  // True server COUNT (T-303) — not the capped row length.
  const lowStockCount: number = lowStock?.total ?? lowStock?.data?.length ?? 0;

  function handleExport() {
    const rows = (products?.data ?? []);
    if (!rows.length) { toast.error('No data to export'); return; }
    const headers = ['Name', 'SKU', 'Category', 'Stock', 'Cost Price', 'Selling Price', 'Reorder Point'];
    const csvRows = rows.map((p) => [
      p.name,
      p.sku ?? '',
      p.categoryName ?? p.category?.name ?? '',
      p.stockQuantity ?? 0,
      p.costPrice ?? 0,
      p.sellingPrice ?? p.price ?? 0,
      p.reorderPoint ?? '',
    ]);
    const escape = (v: string | number | null | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [headers, ...csvRows].map((r) => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const tabs = [
    { key: 'stock' as const, label: t('stockLevels'), count: null },
    { key: 'movements' as const, label: t('movementHistory'), count: null },
    { key: 'alerts' as const, label: t('reorderAlerts'), count: (reorderAlertsTotal ?? reorderAlertRows.length) || null },
    { key: 'forecast' as const, label: 'Demand Forecast', count: null },
    { key: 'expiry' as const, label: t('expiryTrackingTab'), count: null },
  ];

  return (
    <div className="space-y-6 page-enter">
      {/* Header */}
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <>
            {canViewLocations && selectedLocationId && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary-text border border-primary/30">
                <MapPin className="h-3 w-3" />
                {selectedLocationName ?? 'Location'}
              </span>
            )}
            <Button
              size="sm"
              variant="gradient"
              onClick={() => setAdjustOpen(true)}
              style={{ display: canAdjust ? undefined : 'none' }}
            >
              <Plus className="me-2 h-4 w-4" />
              {t('stockAdjustmentTitle')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="border-primary/30 hover:bg-primary/10 transition-colors duration-200">
              <Download className="me-2 h-4 w-4" />
              {t('export')}
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          className="animate-fade-in-up stagger-1"
          label={t('totalProducts')}
          value={products?.meta?.total || 0}
          format="number"
          icon={Boxes}
          accent="info"
        />
        <StatCard
          className="animate-fade-in-up stagger-2"
          label={t('lowStockAlerts')}
          value={lowStockCount}
          format="number"
          icon={AlertTriangle}
          accent="warning"
        />
        <StatCard
          className="animate-fade-in-up stagger-3"
          label={t('recentMovements')}
          value={recentMovementsTotal}
          format="number"
          icon={ArrowUp}
          accent="default"
        />
      </div>

      {/* Tabbed content */}
      <Card className="animate-fade-in-up stagger-4">
        {/* Tab bar */}
        <TabBar tabs={tabs} activeTab={activeTab} onTabChange={(key) => setActiveTab(key as typeof activeTab)} />

        {/* Tab: Stock Levels */}
        {activeTab === 'stock' && (
          <div>
            <div className="flex items-center justify-between p-4">
              <p className="text-sm text-muted-foreground">All products and their current stock levels</p>
              <SearchInput
                value={search}
                onChange={(value) => { setSearch(value); setPage(1); }}
                placeholder={t('searchByProductName')}
                className="w-64"
              />
            </div>
            <CardContent className="pt-0">
              {isLoading ? (
                <SkeletonTable rows={8} cols={6} className="mt-2" />
              ) : !products?.data?.length ? (
                <EmptyState icon={Package} title={t('noProductsFound')} description={t('noInventoryMatchSearch')} />
              ) : (
                <>
                  <Table mobileCards>
                    <THead>
                      <Tr hover={false}>
                        <Th>{t('product')}</Th>
                        <Th>{t('sku')}</Th>
                        <Th className="text-end">
                          {canViewLocations && selectedLocationId ? t('locationStock') : t('totalStock')}
                        </Th>
                        {canViewLocations && selectedLocationId && (
                          <Th className="text-end text-muted-foreground/60">{t('global')}</Th>
                        )}
                        <Th className="text-end">{t('cost')}</Th>
                        <Th className="text-end">{t('price')}</Th>
                        <Th className="text-center">{t('status')}</Th>
                      </Tr>
                    </THead>
                    <TBody>
                      {products?.data?.map((p) => {
                        const locQty = canViewLocations && selectedLocationId ? (locationStockMap.get(p.id) ?? 0) : null;
                        const displayQty = locQty !== null ? locQty : p.stockQuantity;
                        return (
                          <Tr key={p.id}>
                            <Td label={t('product')} className="font-medium td-name">{p.name}</Td>
                            <Td label={t('sku')} className="text-muted-foreground td-hide">{p.sku || '—'}</Td>
                            <Td label={canViewLocations && selectedLocationId ? t('locationStock') : t('totalStock')} className={`text-end font-mono ${displayQty <= 5 ? 'text-destructive-text font-semibold' : displayQty <= 10 ? 'text-warning-text font-semibold' : 'text-success-text'}`}>{displayQty}</Td>
                            {canViewLocations && selectedLocationId && (
                              <Td label={t('global')} className="text-end font-mono text-muted-foreground/60 text-xs td-hide">{p.stockQuantity}</Td>
                            )}
                            <Td label={t('cost')} className="text-end text-muted-foreground td-hide">{formatCurrency(p.costPrice)}</Td>
                            <Td label={t('price')} className="text-end">{formatCurrency(p.salePrice)}</Td>
                            <Td label={t('status')} className="text-center">
                              {displayQty <= 0 ? (
                                <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive-text">{t('outOfStock')}</span>
                              ) : displayQty <= 10 ? (
                                <span className="inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-text">{t('lowStock')}</span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success-text">{t('inStock')}</span>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}
                    </TBody>
                  </Table>
                  {products?.meta && (
                    <div className="mt-4 border-t pt-4">
                      <PaginationBar page={page} pageSize={20} total={products.meta.total ?? 0} onPageChange={setPage} />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </div>
        )}

        {/* Tab: Movement History */}
        {activeTab === 'movements' && (
          <CardContent className="space-y-3 pt-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="text"
                className="flex-1"
                placeholder={t('searchByProductName')}
                value={movSearch}
                onChange={(e) => { setMovSearch(e.target.value); setMovPage(1); }}
              />
              <Dropdown
                value={movType}
                onChange={(e) => { setMovType(e.value); setMovPage(1); }}
                options={[
                  { label: t('allTypes'), value: '' },
                  { label: t('sale'), value: 'SALE' },
                  { label: t('saleReturn'), value: 'RETURN' },
                  { label: t('saleVoid'), value: 'SALE_VOID' },
                  { label: t('purchase'), value: 'PURCHASE' },
                  { label: t('adjustmentInType'), value: 'ADJUSTMENT_IN' },
                  { label: t('adjustmentOutType'), value: 'ADJUSTMENT_OUT' },
                  { label: t('transfer'), value: 'TRANSFER' },
                  { label: t('damage'), value: 'DAMAGE' },
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('allTypes')}
              />
              {canViewLocations && locationsList.length > 0 && (
                <Dropdown
                  value={movLocationId}
                  onChange={(e) => { setMovLocationId(e.value); setMovPage(1); }}
                  options={[{ label: t('allLocations'), value: '' }, ...locationsList.map((loc) => ({ label: loc.name, value: loc.id }))]}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={t('allLocations')}
                />
              )}
              <ExportMovementsButton movSearch={movSearch} movType={movType} movLocationId={movLocationId} />
            </div>
            {movLoading ? (
              <SkeletonTable rows={5} cols={5} />
            ) : (
              <div className="rounded-lg border">
                <Table mobileCards className="mov-table">
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('date')}</Th>
                      <Th>{t('product')}</Th>
                      <Th>{t('type')}</Th>
                      <Th className="mov-narrow-hide">{t('reference')}</Th>
                      {canViewLocations && <Th className="td-hide mov-narrow-hide">{t('location')}</Th>}
                      <Th className="text-end mov-narrow-hide">{t('beforeQty')}</Th>
                      <Th className="text-end">{t('quantityChange')}</Th>
                      <Th className="text-end">{t('afterQty')}</Th>
                      <Th className="mov-narrow-hide">{t('serialNo')}</Th>
                      {canReverse && <Th className="text-end">{t('actionsCol')}</Th>}
                    </Tr>
                  </THead>
                  <TBody>
                    {movRows.length === 0 ? (
                      <Tr hover={false} className="no-card">
                        <Td colSpan={(canViewLocations ? 9 : 8) + (canReverse ? 1 : 0)} className="py-8 text-center text-muted-foreground">{t('noMovementsFound')}</Td>
                      </Tr>
                    ) : (
                      movRows.map((m) => {
                        const positive = m.afterQty != null && m.beforeQty != null
                          ? m.afterQty >= m.beforeQty
                          : ['ADJUSTMENT_IN', 'PURCHASE', 'RETURN'].includes(m.type ?? '');
                        const changeIsDown = movementChangeIsDown(m);
                        const refBlock = (
                          <div className="space-y-0.5">
                                {m.reference &&
                                  (m.reference.type ? (
                                    <div><RecordLink type={m.reference.type as RecordType} id={m.reference.id} label={m.reference.label} variant="mono" className="text-xs" /></div>
                                  ) : (
                                    <div className="font-mono"><bdi dir="ltr">{m.reference.label}</bdi></div>
                                  ))}
                                {m.journalEntry && (
                                  <div>
                                    <RecordLink type="journalEntry" id={m.journalEntry.id} label={m.journalEntry.entryNumber} variant="mono" className="text-xs" />
                                  </div>
                                )}
                                {m.reversal?.reversedBy && (
                                  <div>
                                    {t('reversedByLead')}{' '}
                                    <RecordLink type="stockMovement" id={m.reversal.reversedBy.id} label={new Date(m.reversal.reversedBy.createdAt).toISOString().slice(0, 10)} className="text-xs" />
                                  </div>
                                )}
                                {m.reason && <div className="max-w-xs whitespace-normal break-words">{m.reason}</div>}
                                {!m.reference && !m.journalEntry && !m.reason && !m.reversal?.reversedBy && <span className="opacity-50">—</span>}
                              </div>
                        );
                        return (
                          <Tr key={m.id}>
                            <Td label={t('date')} className="py-2.5 text-muted-foreground text-xs td-name">
                              <RecordLink type="stockMovement" id={m.id} label={new Date(m.createdAt).toLocaleString()} className="text-xs" />
                            </Td>
                            <Td label={t('product')} className="py-2.5 font-medium">
                              {m.product?.name ?? '—'}
                              <div className="mov-narrow-show mt-0.5 text-xs font-normal text-muted-foreground">{refBlock}</div>
                            </Td>
                            <Td label={t('type')} className="py-2.5">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${positive ? 'bg-success/10 text-success-text' : 'bg-destructive/10 text-destructive-text'}`}>
                                {m.type?.replace(/_/g, ' ')}
                              </span>
                              {m.treatment && !m.reversal?.reverses && (
                                <span className={`ms-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${m.treatment === 'WRITE_OFF' ? 'bg-destructive/10 text-destructive-text' : 'bg-info/10 text-info-text'}`}>
                                  {m.treatment === 'OPENING' ? t('badgeOpening') : m.treatment === 'CORRECTION' ? t('badgeCorrection') : t('badgeWriteOff')}
                                </span>
                              )}
                              {m.reversal?.state === 'REVERSED' && (
                                <span className="ms-1 inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{t('badgeReversed')}</span>
                              )}
                              {m.reversal?.reverses && (
                                <span className="ms-1 inline-flex rounded-full bg-info/10 px-2 py-0.5 text-xs font-medium text-info-text">{t('badgeReversal')}</span>
                              )}
                            </Td>
                            <Td label={t('reference')} className="py-2.5 text-xs text-muted-foreground mov-narrow-hide">
                              {refBlock}
                            </Td>
                            {canViewLocations && (
                              <Td label={t('location')} className="py-2.5 text-xs text-muted-foreground td-hide mov-narrow-hide">
                                {m.location?.name ?? <span className="italic opacity-50">—</span>}
                              </Td>
                            )}
                            <Td label={t('beforeQty')} className="py-2.5 text-end tabular-nums text-muted-foreground mov-narrow-hide">{m.beforeQty ?? '—'}</Td>
                            <Td label={t('quantityChange')} className="py-2.5 text-end">
                              <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-bold tabular-nums ${changeIsDown ? 'bg-destructive/10 text-destructive-text' : 'bg-success/10 text-success-text'}`}>
                                {changeIsDown ? <ArrowDown className="h-4 w-4 shrink-0" /> : <ArrowUp className="h-4 w-4 shrink-0" />}
                                {Math.abs(m.quantity)}
                              </span>
                            </Td>
                            <Td label={t('afterQty')} className="py-2.5 text-end font-semibold tabular-nums">{m.afterQty ?? '—'}</Td>
                            <Td label={t('serialNo')} className="py-2.5 text-xs font-mono text-muted-foreground mov-narrow-hide">
                              {m.serialNumber?.serialNo ?? <span className="italic opacity-40">—</span>}
                            </Td>
                            {canReverse && (
                              <Td label={t('actionsCol')} className="py-2.5 text-end">
                                <MovementReverseCell m={m} onReverse={() => setReverseTarget(m)} />
                              </Td>
                            )}
                          </Tr>
                        );
                      })
                    )}
                  </TBody>
                </Table>
              </div>
            )}
            {movTotal > 20 && (
              <div className="border-t pt-3">
                <PaginationBar page={movPage} pageSize={20} total={movTotal} onPageChange={setMovPage} />
              </div>
            )}
          </CardContent>
        )}

        {/* Tab: Reorder Alerts */}
        {activeTab === 'alerts' && (
          <CardContent className="pt-4">
            <ReorderAlertsCard embedded />
          </CardContent>
        )}

        {/* Tab: Demand Forecast */}
        {activeTab === 'forecast' && (
          <CardContent className="pt-4">
            <DemandForecastTab />
          </CardContent>
        )}

        {/* Tab: Expiry Tracking */}
        {activeTab === 'expiry' && (
          <CardContent className="pt-4">
            <ExpiryTrackingTab />
          </CardContent>
        )}
      </Card>

      <StockAdjustmentDialog visible={adjustOpen} onClose={() => setAdjustOpen(false)} />
      <ReverseAdjustmentDialog movement={reverseTarget} onClose={() => setReverseTarget(null)} />
    </div>
  );
}